import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Clock, CalendarDays, FileText, Bell, ClipboardList, Target,
  UserCircle, LogOut, Menu, PlayCircle, Coffee, PlayIcon, StopCircle,
  ArrowLeft, Loader2, ShieldCheck, Upload, Send, KeyRound, User,
  CheckCircle2, TimerReset, Search, Filter, ChevronLeft, ChevronRight,
  Download, Eye, Inbox, BadgeCheck, XCircle, Hourglass, Cake, Umbrella,
  GraduationCap, PartyPopper, ClipboardCheck, MailCheck, AlertCircle,
  Phone, MapPin, IdCard, Building2, Briefcase, Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";
import { employeeApi, useEmployeeAuth, EmployeeGuard } from "../../context/EmployeeAuthContext";
import { useEmployeeHeartbeat } from "../../hooks/useEmployeeHeartbeat";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

/* ============================================================ */
/*  LOGIN                                                       */
/* ============================================================ */
export function EmployeeLogin() {
  const { employee, login } = useEmployeeAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (employee) nav("/employee", { replace: true }); }, [employee, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Bem-vindo(a) ao Portal do Funcionário");
      nav("/employee", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciais inválidas.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-muted/40 px-4" data-testid="employee-login-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-display font-bold">Portal do Funcionário</h1>
            <p className="text-xs text-muted-foreground">Acesso exclusivo dos colaboradores JMS.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="emp-email" className="text-xs">E-mail corporativo</Label>
            <Input
              id="emp-email"
              data-testid="employee-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@jms.com"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="emp-pwd" className="text-xs">Senha</Label>
            <Input
              id="emp-pwd"
              data-testid="employee-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1"
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={submitting}
            data-testid="employee-login-submit"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Entrando</> : "Entrar"}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/login" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Sou administrador
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  LAYOUT                                                      */
/* ============================================================ */
const MENU = [
  { to: "/employee",                icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/employee/timeclock",      icon: Clock,           label: "Controle de Ponto" },
  { to: "/employee/journey",        icon: ClipboardList,   label: "Minha Jornada" },
  { to: "/employee/calendar",       icon: CalendarDays,    label: "Meu Calendário" },
  { to: "/employee/availability",   icon: Clock,           label: "Disponibilidade" },
  { to: "/employee/documents",      icon: FileText,        label: "Meus Documentos" },
  { to: "/employee/requests",       icon: Send,            label: "Minhas Solicitações" },
  // AP-020 · Metas & KPI
  { to: "/employee/metas",          icon: Target,          label: "Minhas Metas" },
  // AP-020 · Meu Financeiro
  { section: "Meu Financeiro" },
  { to: "/employee/payroll/holerites", icon: FileText,     label: "Meus Holerites" },
  { section: "Conta" },
  { to: "/employee/profile",        icon: UserCircle,      label: "Meu Perfil" },
  { to: "/employee/notifications",  icon: Bell,            label: "Notificações" },
];

export function EmployeeLayout() {
  const { employee, logout, refresh } = useEmployeeAuth();
  // AP-076 F3.1 · mantém o funcionário "Online" no Centro de Monitoramento
  // enquanto ele navega no Portal do Funcionário.
  useEmployeeHeartbeat(employee?.id);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const doLogout = () => { logout(); nav("/employee/login", { replace: true }); };
  return (
    <EmployeeGuard>
      <div className="min-h-screen flex bg-muted/30" data-testid="employee-layout">
        {/* Sidebar */}
        <aside className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
                          fixed md:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-border
                          transition-transform`}>
          <div className="p-5 border-b border-border flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">Portal do Funcionário</p>
              <p className="text-[11px] text-muted-foreground truncate">JMS Multi Service</p>
            </div>
          </div>
          <nav className="p-3 space-y-1" data-testid="employee-sidebar">
            {MENU.map((m, idx) => (
              m.section ? (
                <p key={`sec-${idx}`} className="pt-3 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
                   data-testid={`emp-section-${m.section.toLowerCase().replace(/[\s\/]+/g, "-")}`}>
                  {m.section}
                </p>
              ) : (
                <NavLink
                  key={m.to}
                  to={m.to}
                  end={m.end}
                  onClick={() => setOpen(false)}
                  data-testid={`emp-nav-${m.label.toLowerCase().replace(/[\s\/]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                      isActive ? "bg-primary/10 text-primary font-medium border border-primary/30"
                               : "text-foreground hover:bg-muted"
                    }`
                  }
                >
                  <m.icon className="h-4 w-4" strokeWidth={2} />
                  {m.label}
                </NavLink>
              )
            ))}
          </nav>
          <div className="p-3 border-t border-border">
            <button
              onClick={doLogout}
              data-testid="employee-logout"
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </aside>

        {open && <div onClick={() => setOpen(false)} className="md:hidden fixed inset-0 z-30 bg-black/40" />}

        <main className="flex-1 min-w-0">
          <header className="sticky top-0 z-20 bg-white border-b border-border px-4 py-3 flex items-center gap-3">
            <button className="md:hidden" onClick={() => setOpen((v) => !v)} data-testid="employee-menu-toggle">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">
                Olá, {employee?.full_name?.split(" ")[0] || "colaborador"}!
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {employee?.position || "—"} · {employee?.department || "—"}
              </p>
            </div>
            <WorkStatusBadge status={employee?.work_status} />
          </header>
          <div className="p-4 md:p-6 space-y-6">
            <Outlet />
          </div>
        </main>
      </div>
      <ForceChangePasswordModal
        open={!!employee?.must_change_password}
        onDone={async () => { await refresh(); }}
      />
    </EmployeeGuard>
  );
}

/* ============================================================ */
/*  SHARED UI PRIMITIVES (AP-018.1 · portal design system)      */
/* ============================================================ */
function PageHeader({ title, subtitle, icon: Icon, right }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="portal-page-header">
      <div className="flex items-start gap-3 min-w-0">
        {Icon && (
          <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/30 grid place-items-center shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight truncate">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone = "primary", hint, testid, onClick }) {
  const toneCls = {
    primary: "bg-primary/10 text-primary border-primary/30",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    sky:     "bg-sky-50 text-sky-700 border-sky-200",
    violet:  "bg-violet-50 text-violet-700 border-violet-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  }[tone] || "bg-primary/10 text-primary border-primary/30";
  const clickable = typeof onClick === "function";
  return (
    <div data-testid={testid}
         onClick={onClick}
         className={`rounded-2xl border border-border bg-white p-4 flex items-center gap-3 ${clickable ? "cursor-pointer hover:border-primary/40 hover:shadow-sm transition" : ""}`}>
      <div className={`h-11 w-11 rounded-xl border grid place-items-center shrink-0 ${toneCls}`}>
        {Icon ? <Icon className="h-5 w-5" /> : null}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</p>
        <p className="text-lg font-semibold tabular-nums leading-tight">{value}</p>
        {hint && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{hint}</p>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:   { label: "Pendente",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
    review:    { label: "Em análise", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    approved:  { label: "Aprovada",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected:  { label: "Rejeitada", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    cancelled: { label: "Cancelada", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    done:      { label: "Concluída", cls: "bg-primary/10 text-primary border-primary/30" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-muted text-muted-foreground border-border" };
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${m.cls}`}
                data-testid={`status-${status}`}>{m.label}</span>;
}

function EmptyState({ label, hint, icon: Icon = Inbox, testid = "portal-empty" }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-white p-8 text-center" data-testid={testid}>
      <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 border border-primary/30 grid place-items-center">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <p className="text-sm font-semibold mt-3">{label}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function SkeletonList({ n = 4, testid = "skeleton-list" }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-2xl" />
      ))}
    </div>
  );
}

/** Filtro por período — Hoje / Semana / Mês / Personalizado */
function DateRangeFilter({ value, onChange, testid = "range-filter" }) {
  const set = (kind) => {
    const today = new Date(); today.setHours(0,0,0,0);
    if (kind === "today") return onChange({ kind, from: iso(today), to: iso(today) });
    if (kind === "week") {
      const d = new Date(today); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd);
      const end = new Date(d); end.setDate(end.getDate() + 6);
      return onChange({ kind, from: iso(d), to: iso(end) });
    }
    if (kind === "month") {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return onChange({ kind, from: iso(from), to: iso(to) });
    }
    onChange({ kind: "custom", from: value?.from || "", to: value?.to || "" });
  };
  const iso = (d) => d.toISOString().slice(0, 10);
  const btn = (kind, label) => (
    <Button size="sm" variant={value?.kind === kind ? "default" : "outline"}
            onClick={() => set(kind)} data-testid={`${testid}-${kind}`}>
      {label}
    </Button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {btn("today", "Hoje")}
      {btn("week", "Semana")}
      {btn("month", "Mês")}
      {btn("custom", "Intervalo")}
      {value?.kind === "custom" && (
        <>
          <Input type="date" value={value.from || ""} onChange={(e) => onChange({ ...value, from: e.target.value })}
                 className="h-8 w-[150px]" data-testid={`${testid}-from`} />
          <span className="text-xs text-muted-foreground">até</span>
          <Input type="date" value={value.to || ""} onChange={(e) => onChange({ ...value, to: e.target.value })}
                 className="h-8 w-[150px]" data-testid={`${testid}-to`} />
        </>
      )}
    </div>
  );
}

/* ============================================================ */
/*  FORCE CHANGE PASSWORD (AP-019)                              */
/* ============================================================ */
function ForceChangePasswordModal({ open, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) { toast.error("Preencha todos os campos."); return; }
    if (next.length < 8) { toast.error("Nova senha precisa de 8+ caracteres."); return; }
    if (next !== confirm) { toast.error("As senhas não coincidem."); return; }
    setSaving(true);
    try {
      await employeeApi.patch("/employee-portal/me", { current_password: current, password: next });
      toast.success("Senha atualizada com sucesso.");
      setCurrent(""); setNext(""); setConfirm("");
      await onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível alterar a senha.");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" data-testid="force-change-password-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 border border-border shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-display font-bold">Defina sua nova senha</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Por segurança, você precisa alterar a senha temporária antes de continuar usando o Portal do Funcionário.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Senha atual (temporária)</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                   data-testid="force-change-current" className="mt-1" required />
          </div>
          <div>
            <Label className="text-xs">Nova senha</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                   data-testid="force-change-new" minLength={8} className="mt-1" required />
          </div>
          <div>
            <Label className="text-xs">Confirme a nova senha</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   data-testid="force-change-confirm" minLength={8} className="mt-1" required />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="force-change-submit">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvando</> : "Salvar nova senha"}
          </Button>
        </form>
      </div>
    </div>
  );
}

const WS_LABELS = { off: "Fora de expediente", working: "Trabalhando", on_break: "Em pausa", finished: "Encerrado" };
const WS_TONE = {
  off: "bg-slate-50 text-slate-700 border-slate-200",
  working: "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_break: "bg-amber-50 text-amber-700 border-amber-200",
  finished: "bg-primary/10 text-primary border-primary/30",
};
function WorkStatusBadge({ status }) {
  const s = status || "off";
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${WS_TONE[s]}`} data-testid="employee-work-status">
      {WS_LABELS[s]}
    </Badge>
  );
}

/* ============================================================ */
/*  helpers                                                     */
/* ============================================================ */
const hm = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}h${String(m % 60).padStart(2, "0")}`;
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};

function useToday() {
  const [d, setD] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setD(new Date()), 30000); return () => clearInterval(t); }, []);
  return d;
}

/**
 * Recalcula localmente o resumo da jornada (horas trabalhadas, restantes,
 * horas extras) a cada segundo enquanto o expediente está aberto — sem
 * depender de nova chamada ao backend. Fonte da verdade: os timestamps ISO
 * retornados pelo backend em `summary.start_shift/break_start/break_end/end_shift`.
 */
function useLiveSummary(serverSummary) {
  const [tick, setTick] = useState(0);
  const finished = !!serverSummary?.end_shift;
  const started = !!serverSummary?.start_shift;
  useEffect(() => {
    if (!started || finished) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [started, finished, serverSummary?.start_shift, serverSummary?.break_start, serverSummary?.break_end]);
  return React.useMemo(() => {
    if (!serverSummary) return null;
    const expected = serverSummary.expected_minutes || 480;
    const startShift = serverSummary.start_shift ? new Date(serverSummary.start_shift) : null;
    const endShift = serverSummary.end_shift ? new Date(serverSummary.end_shift) : null;
    const breakStart = serverSummary.break_start ? new Date(serverSummary.break_start) : null;
    const breakEnd = serverSummary.break_end ? new Date(serverSummary.break_end) : null;
    if (!startShift) {
      return { ...serverSummary, worked_minutes: 0, break_minutes: 0,
        remaining_minutes: expected, overtime_minutes: 0, status: "not_started" };
    }
    const now = new Date();
    const end = endShift || now;
    const total = Math.max(0, Math.floor((end - startShift) / 60000));
    let brk = 0;
    if (breakStart && breakEnd) brk = Math.max(0, Math.floor((breakEnd - breakStart) / 60000));
    else if (breakStart && !endShift) brk = Math.max(0, Math.floor((now - breakStart) / 60000));
    const worked = Math.max(0, total - brk);
    const remaining = Math.max(0, expected - worked);
    const overtime = Math.max(0, worked - expected);
    const onBreakNow = !!breakStart && !breakEnd && !endShift;
    const status = endShift ? "finished" : onBreakNow ? "on_break" : "working";
    return { ...serverSummary, worked_minutes: worked, break_minutes: brk,
             remaining_minutes: remaining, overtime_minutes: overtime, status,
             _tick: tick };
  }, [serverSummary, tick]);
}

async function callTimeclock(action) {
  const { data } = await employeeApi.post(`/employee-portal/timeclock/${action}`);
  return data;
}

/* ============================================================ */
/*  DASHBOARD                                                   */
/* ============================================================ */
export function EmployeeDashboard() {
  const { employee, refresh } = useEmployeeAuth();
  const [today, setToday] = useState(null);
  const [loadingToday, setLoadingToday] = useState(true);
  const [kpis, setKpis] = useState({ pending: 0, docs: 0, unread: 0, nextShift: null });
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [busy, setBusy] = useState(false);
  const now = useToday();
  const nav = useNavigate();

  const load = async () => {
    setLoadingToday(true);
    try {
      const { data } = await employeeApi.get("/employee-portal/timeclock/today");
      setToday(data);
    } finally { setLoadingToday(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Carrega KPIs adicionais em paralelo (não bloqueia o timeclock)
    (async () => {
      try {
        const [reqs, docs, notes, cal] = await Promise.all([
          employeeApi.get("/employee-portal/requests").catch(() => ({ data: { items: [] } })),
          employeeApi.get("/employee-portal/documents").catch(() => ({ data: { items: [] } })),
          employeeApi.get("/employee-portal/notifications").catch(() => ({ data: { items: [] } })),
          employeeApi.get("/employee-portal/calendar").catch(() => ({ data: {} })),
        ]);
        const readMap = readNotesMap();
        const unread = (notes.data.items || []).filter((n) => !readMap[n.id]).length;
        const pending = (reqs.data.items || []).filter((r) => ["pending", "review"].includes(r.status)).length;
        setKpis({
          pending,
          docs: (docs.data.items || []).length,
          unread,
          nextShift: computeNextShift(cal.data?.work_schedule),
        });
      } finally { setLoadingKpis(false); }
    })();
  }, []);

  const summary = useLiveSummary(today?.summary);
  const status = summary?.status || "not_started";

  const action = async (kind, label) => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await callTimeclock(kind);
      setToday(data);
      await refresh();
      toast.success(`${label} registrado(a).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível registrar.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="employee-page-dashboard" className="space-y-6">
      {/* ID + Relógio */}
      <div className="rounded-2xl border border-border bg-white p-5 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="h-20 w-20 rounded-full border border-border bg-muted grid place-items-center overflow-hidden">
          {employee?.photo_data_url
            ? <img src={employee.photo_data_url} alt={employee.full_name} className="w-full h-full object-cover" />
            : <User className="h-8 w-8 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-display font-bold truncate">{employee?.full_name || "—"}</h1>
          <p className="text-sm text-muted-foreground truncate">
            {employee?.position || "—"} · {employee?.department || "—"}
          </p>
          <div className="mt-2"><WorkStatusBadge status={employee?.work_status} /></div>
        </div>
        <div className="text-right">
          <p className="text-3xl md:text-4xl font-semibold tabular-nums leading-none" data-testid="live-clock">
            {now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </p>
          <p className="text-[11px] text-muted-foreground mt-2 capitalize">
            {now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </p>
        </div>
      </div>

      {/* KPI cards */}
      {loadingKpis ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="dashboard-kpis">
          <KpiCard label="Expediente" value={STATUS_TEXT[status] || "—"}
                   icon={statusIcon(status)} tone={statusTone(status)}
                   testid="kpi-current-shift" />
          <KpiCard label="Horas hoje" value={hm(summary?.worked_minutes)}
                   icon={Clock} tone="emerald" testid="kpi-worked-today" />
          <KpiCard label="Horas restantes" value={status === "finished" ? "—" : hm(summary?.remaining_minutes)}
                   icon={Hourglass} tone="sky" testid="kpi-remaining" />
          <KpiCard label="Próximo expediente" value={kpis.nextShift?.label || "—"}
                   hint={kpis.nextShift?.hint} icon={CalendarIcon} tone="violet"
                   onClick={() => nav("/employee/calendar")} testid="kpi-next-shift" />
          <KpiCard label="Solicitações pendentes" value={kpis.pending}
                   icon={Send} tone="amber" testid="kpi-pending"
                   onClick={() => nav("/employee/requests")} />
          <KpiCard label="Documentos" value={kpis.docs}
                   icon={FileText} tone="slate" testid="kpi-docs"
                   onClick={() => nav("/employee/documents")} />
          <KpiCard label="Notificações" value={kpis.unread}
                   hint={kpis.unread > 0 ? "não lidas" : "todas lidas"}
                   icon={Bell} tone={kpis.unread > 0 ? "rose" : "primary"}
                   onClick={() => nav("/employee/notifications")} testid="kpi-unread" />
        </div>
      )}

      {/* Time clock actions — botões contextuais conforme o estado atual */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="tc-actions-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Controle de ponto</h3>
          <ShiftStatusPill status={status} />
        </div>

        {loadingToday ? (
          <Skeleton className="h-10 w-full md:w-56" />
        ) : status === "finished" ? (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3"
               data-testid="tc-finished-banner">
            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-primary">Expediente encerrado</p>
              <p className="text-xs text-muted-foreground mt-1">
                Você já registrou o encerramento hoje. Todas as ações estão desabilitadas até amanhã.
              </p>
            </div>
          </div>
        ) : status === "not_started" ? (
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white w-full md:w-auto"
            disabled={busy}
            onClick={() => action("start-shift", "Início do expediente")}
            data-testid="tc-start-shift"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />}
            Iniciar Expediente
          </Button>
        ) : status === "on_break" ? (
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-white w-full md:w-auto"
            disabled={busy}
            onClick={() => action("end-break", "Retorno da pausa")}
            data-testid="tc-end-break"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayIcon className="h-4 w-4 mr-1" />}
            Voltar da Pausa
          </Button>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button variant="outline" disabled={busy}
                    onClick={() => action("start-break", "Início da pausa")}
                    data-testid="tc-start-break">
              <Coffee className="h-4 w-4 mr-1" /> Iniciar Pausa
            </Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                    disabled={busy}
                    onClick={() => action("end-shift", "Encerramento do expediente")}
                    data-testid="tc-end-shift">
              <StopCircle className="h-4 w-4 mr-1" /> Encerrar Expediente
            </Button>
          </div>
        )}
      </div>

      <TodaySummaryCard summary={summary} loading={loadingToday} />
    </div>
  );
}

const STATUS_TEXT = { not_started: "Não iniciado", working: "Ativo", on_break: "Em pausa", finished: "Encerrado" };
function statusIcon(s) { return s === "working" ? PlayCircle : s === "on_break" ? Coffee : s === "finished" ? CheckCircle2 : Hourglass; }
function statusTone(s)  { return s === "working" ? "emerald" : s === "on_break" ? "amber" : s === "finished" ? "primary" : "slate"; }
function readNotesMap() {
  try { return JSON.parse(localStorage.getItem("jms_emp_read_notes") || "{}"); }
  catch { return {}; }
}
function saveReadNotes(map) {
  try { localStorage.setItem("jms_emp_read_notes", JSON.stringify(map)); } catch {}
}
function computeNextShift(ws) {
  if (!ws || !(ws.weekdays || []).length) return null;
  const wdKey = ["sun","mon","tue","wed","thu","fri","sat"];
  const wdLabel = { mon: "Segunda", tue: "Terça", wed: "Quarta", thu: "Quinta", fri: "Sexta", sat: "Sábado", sun: "Domingo" };
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(now); d.setDate(d.getDate() + i);
    if ((ws.weekdays || []).includes(wdKey[d.getDay()])) {
      if (i === 0) return { label: `Hoje · ${ws.start_time || "—"}`, hint: `até ${ws.end_time || "—"}` };
      if (i === 1) return { label: `Amanhã · ${ws.start_time || "—"}`, hint: wdLabel[wdKey[d.getDay()]] };
      return { label: `${wdLabel[wdKey[d.getDay()]]} · ${ws.start_time || "—"}`,
               hint: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) };
    }
  }
  return null;
}

function ShiftStatusPill({ status }) {
  const map = {
    not_started: { label: "Não iniciado", cls: "bg-muted text-muted-foreground border-border" },
    working:     { label: "Em expediente", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    on_break:    { label: "Em pausa", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    finished:    { label: "Encerrado", cls: "bg-primary/10 text-primary border-primary/30" },
  };
  const m = map[status] || map.not_started;
  return (
    <span className={`rounded-md border px-2.5 py-1 text-[11px] font-medium ${m.cls}`}
          data-testid={`tc-status-${status}`}>
      {m.label}
    </span>
  );
}

function TodaySummaryCard({ summary, loading }) {
  const s = summary || {};
  const isFinished = s.status === "finished";
  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-white p-5 space-y-3" data-testid="today-summary-skeleton">
        <Skeleton className="h-4 w-40" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0,1,2,3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[0,1,2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="today-summary">
      <h3 className="text-sm font-semibold mb-3">Minha jornada de hoje</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniRow label="Início" value={fmtTime(s.start_shift)} testid="row-start" />
        <MiniRow label="Pausa" value={fmtTime(s.break_start)} testid="row-break-start" />
        <MiniRow label="Retorno" value={fmtTime(s.break_end)} testid="row-break-end" />
        <MiniRow label="Fim" value={fmtTime(s.end_shift)} testid="row-end" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <MiniRow label="Horas trabalhadas"
                 value={hm(s.worked_minutes)}
                 tone="text-primary" testid="row-worked" />
        <MiniRow label="Horas restantes"
                 value={isFinished ? "—" : hm(s.remaining_minutes)}
                 testid="row-remaining" />
        <MiniRow label="Horas extras"
                 value={hm(s.overtime_minutes)}
                 tone={(s.overtime_minutes || 0) > 0 ? "text-amber-600" : ""}
                 testid="row-overtime" />
      </div>
      {(s.break_minutes || 0) > 0 && (
        <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5" data-testid="row-break-total">
          <TimerReset className="h-3 w-3" /> Tempo total de pausa: <strong>{hm(s.break_minutes)}</strong>
        </p>
      )}
    </div>
  );
}

function MiniRow({ label, value, tone = "", testid }) {
  return (
    <div className="rounded-xl border border-border p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`text-lg font-semibold tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}

/* ============================================================ */
/*  TIMECLOCK PAGE                                              */
/* ============================================================ */
export function EmployeeTimeclock() {
  return (
    <div data-testid="employee-page-timeclock" className="space-y-6">
      <PageHeader title="Controle de Ponto"
                  subtitle="Registre início, pausa, retorno e encerramento do seu expediente."
                  icon={Clock} />
      <EmployeeDashboardShortcut />
    </div>
  );
}

// Reuses the dashboard's time-clock section so employees can also register from
// the dedicated Controle de Ponto page.
function EmployeeDashboardShortcut() {
  return <EmployeeDashboard />;
}

/* ============================================================ */
/*  MINHA JORNADA                                               */
/* ============================================================ */
export function EmployeeJourney() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [range, setRange] = useState(() => ({ kind: "month",
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10),
    to:   new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).toISOString().slice(0,10) }));
  useEffect(() => {
    setLoading(true);
    employeeApi.get("/employee-portal/journey?days=180")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(() => {
    const from = range?.from ? new Date(range.from + "T00:00") : null;
    const to = range?.to ? new Date(range.to + "T23:59") : null;
    return items
      .filter((d) => {
        const dt = new Date(d.day + "T00:00");
        if (from && dt < from) return false;
        if (to && dt > to) return false;
        if (q) return d.day.includes(q);
        return true;
      })
      .sort((a, b) => (a.day < b.day ? 1 : -1));
  }, [items, q, range]);
  const totals = useMemo(() => filtered.reduce((acc, d) => ({
    worked:   acc.worked   + (d.summary.worked_minutes   || 0),
    overtime: acc.overtime + (d.summary.overtime_minutes || 0),
    days:     acc.days     + 1,
  }), { worked: 0, overtime: 0, days: 0 }), [filtered]);
  const dayStatus = (s) => {
    if (s.end_shift) return "done";
    if (s.start_shift && !s.end_shift) return s.break_start && !s.break_end ? "on_break" : "working";
    return "not_started";
  };
  const statusLabel = { done: "Concluído", working: "Em andamento", on_break: "Em pausa", not_started: "Sem registro" };
  const statusCls = {
    done: "bg-primary/10 text-primary border-primary/30",
    working: "bg-emerald-50 text-emerald-700 border-emerald-200",
    on_break: "bg-amber-50 text-amber-700 border-amber-200",
    not_started: "bg-muted text-muted-foreground border-border",
  };
  return (
    <div data-testid="employee-page-journey" className="space-y-5">
      <PageHeader title="Minha Jornada" subtitle="Histórico completo de marcações e horas trabalhadas." icon={ClipboardList} />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Horas trabalhadas" value={hm(totals.worked)} icon={Clock} tone="emerald" testid="journey-kpi-worked" />
        <KpiCard label="Horas extras" value={hm(totals.overtime)} icon={TimerReset} tone="amber" testid="journey-kpi-overtime" />
        <KpiCard label="Dias no período" value={totals.days} icon={CalendarDays} tone="primary" testid="journey-kpi-days" />
      </div>

      <div className="rounded-2xl border border-border bg-white p-4 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="relative w-full md:max-w-xs">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Buscar por data (AAAA-MM-DD)" className="pl-8"
                   data-testid="journey-search" />
          </div>
          <DateRangeFilter value={range} onChange={setRange} testid="journey-range" />
        </div>
      </div>

      {loading ? <SkeletonList n={5} testid="journey-loading" />
       : filtered.length === 0 ? <EmptyState label="Nenhuma marcação no período selecionado"
                                              hint="Ajuste o filtro ou registre uma nova marcação no controle de ponto." icon={ClipboardList} />
       : (
        <div className="space-y-2" data-testid="journey-list">
          {filtered.map((d) => {
            const st = dayStatus(d.summary);
            return (
              <div key={d.day} className="rounded-2xl border border-border bg-white p-4" data-testid={`journey-day-${d.day}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold">{fmtDate(d.day)}</p>
                    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${statusCls[st]}`}>{statusLabel[st]}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span>Total: <b className="font-mono">{hm(d.summary.worked_minutes)}</b></span>
                    {(d.summary.overtime_minutes || 0) > 0 &&
                      <span className="text-amber-600">Extras: <b className="font-mono">{hm(d.summary.overtime_minutes)}</b></span>}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[11px] text-muted-foreground">
                  <span>Início: <b className="text-foreground">{fmtTime(d.summary.start_shift)}</b></span>
                  <span>Pausa: <b className="text-foreground">{fmtTime(d.summary.break_start)}</b></span>
                  <span>Retorno: <b className="text-foreground">{fmtTime(d.summary.break_end)}</b></span>
                  <span>Fim: <b className="text-foreground">{fmtTime(d.summary.end_shift)}</b></span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  CALENDAR                                                    */
/* ============================================================ */
const WD_LABELS = { mon: "Seg", tue: "Ter", wed: "Qua", thu: "Qui", fri: "Sex", sat: "Sáb", sun: "Dom" };
const WDKEY = ["sun","mon","tue","wed","thu","fri","sat"];

export function EmployeeCalendar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appts, setAppts] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      employeeApi.get("/employee-portal/calendar"),
      employeeApi.get("/employee-portal/appointments?page_size=200"),
    ]).then(([cal, ap]) => {
      if (cal.status === "fulfilled") setData(cal.value.data);
      if (ap.status === "fulfilled") setAppts(ap.value.data?.items || []);
    }).finally(() => setLoading(false));
  }, []);
  const ws = data?.work_schedule || {};

  // Marca dias
  const marks = useMemo(() => {
    const m = {};
    // Feriados — vêm como array de strings "AAAA-MM-DD" ou objetos {date, name}
    (data?.holidays || []).forEach((h) => {
      const iso = typeof h === "string" ? h : h?.date;
      const name = typeof h === "string" ? h : h?.name;
      if (iso) { m[iso] = m[iso] || []; m[iso].push({ kind: "holiday", label: name || "Feriado" }); }
    });
    // Ausências aprovadas
    (data?.approved_leaves || []).forEach((l) => {
      const start = new Date(l.starts_on); const end = new Date(l.ends_on || l.starts_on);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = d.toISOString().slice(0, 10);
        const map = { vacation: "vacation", medical: "leave", leave: "leave" }[l.type] || "leave";
        m[iso] = m[iso] || []; m[iso].push({ kind: map, label: l.subject || l.type });
      }
    });
    // Eventos internos & treinamentos (se o backend expor)
    (data?.events || []).forEach((ev) => {
      if (!ev?.date) return;
      m[ev.date] = m[ev.date] || [];
      m[ev.date].push({ kind: ev.kind || "event", label: ev.title || "Evento" });
    });
    return m;
  }, [data]);

  const days = useMemo(() => buildMonthGrid(cursor.y, cursor.m), [cursor]);
  const monthLabel = new Date(cursor.y, cursor.m, 1)
    .toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  const prev = () => setCursor(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  const next = () => setCursor(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  const today = () => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); };

  return (
    <div data-testid="employee-page-calendar" className="space-y-5">
      <PageHeader title="Meu Calendário" subtitle="Sua jornada, feriados, férias e eventos internos em um só lugar."
                  icon={CalendarDays}
                  right={<div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" onClick={prev} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={today} data-testid="cal-today">Hoje</Button>
                    <Button size="icon" variant="outline" onClick={next} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
                  </div>} />

      {/* AP-082 · Meus Agendamentos (do funcionário logado) */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="emp-appointments-list">
        <h3 className="text-sm font-semibold mb-3">Meus Agendamentos</h3>
        {loading ? (
          <p className="text-xs text-slate-400 italic">Carregando…</p>
        ) : appts.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Nenhum agendamento atribuído a você.</p>
        ) : (
          <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {appts.map((a) => (
              <li key={a.id} data-testid={`emp-appt-${a.id}`} className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 flex items-start gap-2">
                <CalendarDays className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{a.customer_name || "Cliente"}</span>
                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold border ${
                      a.status === "confirmed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      a.status === "scheduled" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      a.status === "in_progress" ? "bg-sky-50 text-sky-700 border-sky-200" :
                      a.status === "done" ? "bg-slate-100 text-slate-700 border-slate-200" :
                      a.status === "cancelled" ? "bg-rose-50 text-rose-700 border-rose-200" :
                      "bg-slate-50 text-slate-600 border-slate-200"
                    }`}>{a.status || "—"}</span>
                  </div>
                  <p className="text-slate-500 truncate">{a.service_name || "Agendamento"}</p>
                  <p className="text-[11px] text-slate-400 tabular-nums">
                    {new Date(a.start_at).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" })}
                    {a.duration_min ? ` · ${a.duration_min} min` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Jornada semanal (mesmo card) */}
      <div className="rounded-2xl border border-border bg-white p-5">
        <h3 className="text-sm font-semibold mb-3">Jornada semanal</h3>
        <div className="grid grid-cols-7 gap-2">
          {["mon","tue","wed","thu","fri","sat","sun"].map((wd) => {
            const active = (ws.weekdays || []).includes(wd);
            return (
              <div key={wd} className={`rounded-xl border p-3 text-center ${active ? "bg-primary/5 border-primary/30" : "bg-muted/30 border-border"}`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{WD_LABELS[wd]}</p>
                <p className="text-sm font-semibold mt-0.5">
                  {active ? `${ws.start_time || "—"}–${ws.end_time || "—"}` : "Folga"}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Calendário mensal */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="cal-monthly">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold capitalize">{monthLabel}</h3>
          <CalendarLegend />
        </div>
        {loading ? (
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 42 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground mb-2">
              {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, i) => {
                const iso = d.date.toISOString().slice(0, 10);
                const isToday = iso === new Date().toISOString().slice(0, 10);
                const inMonth = d.date.getMonth() === cursor.m;
                const dayMarks = marks[iso] || [];
                const wdKey = WDKEY[d.date.getDay()];
                const isWorkday = (ws.weekdays || []).includes(wdKey);
                return (
                  <div key={i}
                       data-testid={`cal-cell-${iso}`}
                       className={`min-h-[76px] rounded-lg border p-1.5 flex flex-col gap-1 ${
                         !inMonth ? "bg-muted/20 border-border/40 opacity-50" :
                         isToday ? "border-primary bg-primary/5 ring-1 ring-primary/40" :
                         "bg-white border-border"
                       }`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${isToday ? "text-primary" : ""}`}>{d.date.getDate()}</span>
                      {inMonth && isWorkday && dayMarks.length === 0 &&
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/40" title="Expediente" />}
                    </div>
                    <div className="flex flex-wrap gap-0.5">
                      {dayMarks.slice(0, 3).map((m, idx) => (
                        <span key={idx} title={m.label}
                              className={`text-[9px] rounded-sm px-1 border ${
                                m.kind === "holiday" ? "bg-rose-50 text-rose-700 border-rose-200" :
                                m.kind === "vacation" ? "bg-sky-50 text-sky-700 border-sky-200" :
                                m.kind === "leave"   ? "bg-amber-50 text-amber-700 border-amber-200" :
                                m.kind === "training"? "bg-violet-50 text-violet-700 border-violet-200" :
                                                        "bg-slate-50 text-slate-700 border-slate-200"
                              }`}>
                          {m.label.length > 10 ? m.label.slice(0, 9) + "…" : m.label}
                        </span>
                      ))}
                      {dayMarks.length > 3 && <span className="text-[9px] text-muted-foreground">+{dayMarks.length - 3}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Listas complementares */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-white p-5">
          <h3 className="text-sm font-semibold mb-2">Feriados da empresa</h3>
          {(data?.holidays || []).length === 0
            ? <EmptyState label="Nenhum feriado cadastrado" icon={PartyPopper} testid="cal-empty-holidays" />
            : <ul className="text-sm space-y-1">
                {(data.holidays || []).map((h, i) => {
                  const iso = typeof h === "string" ? h : h?.date;
                  const name = typeof h === "string" ? h : h?.name;
                  return <li key={i} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-rose-500" /> {name || iso} <span className="text-xs text-muted-foreground">{fmtDate(iso)}</span>
                  </li>;
                })}
              </ul>}
        </div>
        <div className="rounded-2xl border border-border bg-white p-5">
          <h3 className="text-sm font-semibold mb-2">Ausências aprovadas</h3>
          {(data?.approved_leaves || []).length === 0
            ? <EmptyState label="Nenhuma ausência aprovada" icon={Umbrella} testid="cal-empty-leaves" />
            : <ul className="text-sm space-y-1">{data.approved_leaves.map((l) => (
                <li key={l.id} className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${l.type === "vacation" ? "bg-sky-500" : "bg-amber-500"}`} />
                  {l.subject} <span className="text-xs text-muted-foreground">— {fmtDate(l.starts_on)} → {fmtDate(l.ends_on)}</span>
                </li>
              ))}</ul>}
        </div>
      </div>
    </div>
  );
}

function CalendarLegend() {
  const items = [
    { c: "bg-primary/40",   l: "Expediente" },
    { c: "bg-rose-500",     l: "Feriado" },
    { c: "bg-sky-500",      l: "Férias" },
    { c: "bg-amber-500",    l: "Ausência" },
    { c: "bg-violet-500",   l: "Treinamento" },
    { c: "bg-slate-500",    l: "Evento interno" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground" data-testid="cal-legend">
      {items.map((it) => (
        <span key={it.l} className="flex items-center gap-1">
          <span className={`h-2 w-2 rounded-full ${it.c}`} /> {it.l}
        </span>
      ))}
    </div>
  );
}

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay(); // 0=Sun
  const start = new Date(year, month, 1 - startDay);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    days.push({ date: d });
  }
  return days;
}

/* ============================================================ */
/*  DOCUMENTS                                                   */
/* ============================================================ */
const DOC_KIND_LABEL = {
  contract: "Contrato", cpf: "CPF", rg: "RG", proof_address: "Comprovante de endereço",
  medical: "Atestado", diploma: "Diploma/Certificado", policy: "Política interna",
  receipt: "Recibo", other: "Outro",
};
function inferKind(d) {
  const raw = (d.kind || d.category || d.type || "").toLowerCase();
  return DOC_KIND_LABEL[raw] ? raw : "other";
}
function docIcon(fileName) {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (["pdf"].includes(ext)) return FileText;
  if (["png","jpg","jpeg","gif","webp"].includes(ext)) return FileText;
  return FileText;
}

export function EmployeeDocuments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [previewDoc, setPreviewDoc] = useState(null); // { name, mime, url }
  useEffect(() => {
    setLoading(true);
    employeeApi.get("/employee-portal/documents")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return items.filter((d) => {
      const k = inferKind(d);
      if (kind !== "all" && k !== kind) return false;
      if (!kw) return true;
      const name = (d.file_name || d.name || "").toLowerCase();
      return name.includes(kw) || (DOC_KIND_LABEL[k] || "").toLowerCase().includes(kw);
    });
  }, [items, q, kind]);
  const kindOptions = useMemo(() => {
    const set = new Set(items.map(inferKind));
    return Array.from(set);
  }, [items]);
  const preview = async (d) => {
    try {
      const res = await employeeApi.get(`/employee-portal/documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const isPdfOrImage = /^(application\/pdf|image\/)/i.test(res.data.type);
      if (isPdfOrImage) setPreviewDoc({ name: d.file_name || d.name, mime: res.data.type, url });
      else { window.open(url, "_blank", "noopener,noreferrer"); }
    } catch { toast.error("Não foi possível abrir o documento."); }
  };
  const download = async (d) => {
    try {
      const res = await employeeApi.get(`/employee-portal/documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name || d.name || "documento";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao baixar."); }
  };
  return (
    <div data-testid="employee-page-documents" className="space-y-5">
      <PageHeader title="Meus Documentos"
                  subtitle="Central de documentos atribuídos a você pela administração."
                  icon={FileText} />

      <div className="rounded-2xl border border-border bg-white p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Buscar por nome ou categoria" className="pl-8"
                 data-testid="docs-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Button size="sm" variant={kind === "all" ? "default" : "outline"} onClick={() => setKind("all")}
                  data-testid="docs-filter-all">Todos</Button>
          {kindOptions.map((k) => (
            <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}
                    data-testid={`docs-filter-${k}`}>{DOC_KIND_LABEL[k] || k}</Button>
          ))}
        </div>
      </div>

      {loading ? <SkeletonList n={4} testid="docs-loading" />
       : filtered.length === 0 ? <EmptyState label="Nenhum documento encontrado"
                                              hint="Ajuste os filtros ou aguarde a administração enviar seus documentos." icon={FileText} />
       : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="employee-docs-list">
          <div className="hidden md:grid grid-cols-[1fr,140px,140px,120px,120px] gap-3 px-4 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">
            <span>Nome</span><span>Categoria</span><span>Data</span><span>Tipo</span><span className="text-right">Ações</span>
          </div>
          <ul className="divide-y divide-border">
            {filtered.map((d) => {
              const k = inferKind(d);
              const fileName = d.file_name || d.name || "documento";
              const ext = (fileName.split(".").pop() || "—").toUpperCase();
              const Icon = docIcon(fileName);
              return (
                <li key={d.id} className="grid grid-cols-1 md:grid-cols-[1fr,140px,140px,120px,120px] gap-2 md:gap-3 px-4 py-3 items-center"
                    data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary border border-primary/30 grid place-items-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{fileName}</p>
                      <p className="md:hidden text-[10px] text-muted-foreground">{DOC_KIND_LABEL[k]} · {fmtDate(d.created_at)} · {ext}</p>
                    </div>
                  </div>
                  <span className="hidden md:inline text-xs text-muted-foreground">{DOC_KIND_LABEL[k]}</span>
                  <span className="hidden md:inline text-xs text-muted-foreground">{fmtDate(d.created_at)}</span>
                  <span className="hidden md:inline"><Badge variant="secondary" className="text-[10px]">{ext}</Badge></span>
                  <div className="flex md:justify-end items-center gap-1">
                    <TooltipProvider delayDuration={150}>
                      <Tooltip><TooltipTrigger asChild>
                        <Button size="icon" variant="ghost" onClick={() => preview(d)} data-testid={`doc-preview-${d.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Visualizar</TooltipContent></Tooltip>
                      <Tooltip><TooltipTrigger asChild>
                        <Button size="icon" variant="ghost" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Baixar</TooltipContent></Tooltip>
                    </TooltipProvider>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Preview modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => { URL.revokeObjectURL(previewDoc.url); setPreviewDoc(null); }}
             data-testid="doc-preview-modal">
          <div className="bg-white rounded-2xl overflow-hidden w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-border flex items-center justify-between gap-2">
              <p className="text-sm font-semibold truncate">{previewDoc.name}</p>
              <Button size="sm" variant="ghost" onClick={() => { URL.revokeObjectURL(previewDoc.url); setPreviewDoc(null); }}
                      data-testid="doc-preview-close">Fechar</Button>
            </div>
            <div className="flex-1 bg-muted/40">
              {previewDoc.mime.startsWith("image/")
                ? <img src={previewDoc.url} alt="" className="w-full h-full object-contain" />
                : <iframe src={previewDoc.url} title="preview" className="w-full h-full border-0" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  REQUESTS                                                    */
/* ============================================================ */
const REQ_TYPES = [
  { value: "vacation", label: "Férias" },
  { value: "leave",    label: "Ausência" },
  { value: "medical",  label: "Atestado médico" },
  { value: "general",  label: "Solicitação geral" },
];
const REQ_TYPE_LABEL = Object.fromEntries(REQ_TYPES.map((t) => [t.value, t.label]));

export function EmployeeRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("vacation");
  const [subject, setSubject] = useState("");
  const [desc, setDesc] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const load = () => {
    setLoading(true);
    employeeApi.get("/employee-portal/requests")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) { toast.error("Informe um assunto"); return; }
    setSaving(true);
    try {
      await employeeApi.post("/employee-portal/requests",
        { type, subject, description: desc, starts_on: from || null, ends_on: to || null });
      toast.success("Solicitação enviada");
      setSubject(""); setDesc(""); setFrom(""); setTo("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar.");
    } finally { setSaving(false); }
  };
  const counters = useMemo(() => {
    const c = { pending: 0, review: 0, approved: 0, rejected: 0 };
    items.forEach((r) => { if (r.status in c) c[r.status]++; });
    return c;
  }, [items]);
  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return items
      .filter((r) => statusFilter === "all" || r.status === statusFilter)
      .filter((r) => !kw ||
        (r.subject || "").toLowerCase().includes(kw) ||
        (r.description || "").toLowerCase().includes(kw) ||
        (REQ_TYPE_LABEL[r.type] || "").toLowerCase().includes(kw))
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }, [items, q, statusFilter]);
  return (
    <div data-testid="employee-page-requests" className="space-y-5">
      <PageHeader title="Minhas Solicitações"
                  subtitle="Envie pedidos de férias, ausências, atestados e acompanhe o histórico."
                  icon={Send} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Pendentes" value={counters.pending} icon={Hourglass} tone="amber"
                 onClick={() => setStatusFilter("pending")} testid="req-kpi-pending" />
        <KpiCard label="Em análise" value={counters.review} icon={ClipboardCheck} tone="sky"
                 onClick={() => setStatusFilter("review")} testid="req-kpi-review" />
        <KpiCard label="Aprovadas" value={counters.approved} icon={BadgeCheck} tone="emerald"
                 onClick={() => setStatusFilter("approved")} testid="req-kpi-approved" />
        <KpiCard label="Rejeitadas" value={counters.rejected} icon={XCircle} tone="rose"
                 onClick={() => setStatusFilter("rejected")} testid="req-kpi-rejected" />
      </div>

      {/* Formulário */}
      <form onSubmit={submit} className="rounded-2xl border border-border bg-white p-5 space-y-3" data-testid="requests-form">
        <h3 className="text-sm font-semibold">Nova solicitação</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <select data-testid="req-type" value={type} onChange={(e) => setType(e.target.value)}
                    className="mt-1 block w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
              {REQ_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Assunto</Label>
            <Input data-testid="req-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" data-testid="req-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" data-testid="req-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Descrição</Label>
          <Textarea data-testid="req-desc" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1" />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saving} data-testid="req-submit">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enviando</> : <><Send className="h-4 w-4 mr-1" /> Enviar solicitação</>}
          </Button>
        </div>
      </form>

      {/* Filtro + Histórico */}
      <div className="rounded-2xl border border-border bg-white p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Buscar por assunto, descrição ou tipo" className="pl-8"
                 data-testid="req-search" />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {[
            { v: "all", l: "Todas" },
            { v: "pending", l: "Pendentes" },
            { v: "review", l: "Em análise" },
            { v: "approved", l: "Aprovadas" },
            { v: "rejected", l: "Rejeitadas" },
          ].map((f) => (
            <Button key={f.v} size="sm" variant={statusFilter === f.v ? "default" : "outline"}
                    onClick={() => setStatusFilter(f.v)}
                    data-testid={`req-filter-${f.v}`}>{f.l}</Button>
          ))}
        </div>
      </div>

      <div className="space-y-2" data-testid="requests-list">
        {loading ? <SkeletonList n={4} testid="req-loading" />
         : filtered.length === 0 ? <EmptyState label="Nenhuma solicitação encontrada"
                                                hint="Envie uma nova solicitação no formulário acima." icon={Send} />
         : filtered.map((r) => (
          <div key={r.id} className="rounded-2xl border border-border bg-white p-4"
               data-testid={`req-row-${r.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="text-[10px] uppercase">{REQ_TYPE_LABEL[r.type] || r.type}</Badge>
                <p className="text-sm font-semibold">{r.subject}</p>
              </div>
              <StatusBadge status={r.status} />
            </div>
            {r.description && <p className="text-xs text-muted-foreground mt-1">{r.description}</p>}
            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5">
              <CalendarIcon className="h-3 w-3" />
              {r.starts_on ? `${fmtDate(r.starts_on)} → ${fmtDate(r.ends_on || r.starts_on)}` : `Sem período — ${fmtDate(r.created_at)}`}
            </p>
            {/* Mini-timeline */}
            <div className="mt-3 flex items-center gap-2 flex-wrap text-[10px]">
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Enviada <span className="text-foreground">{fmtDate(r.created_at)}</span>
              </span>
              {r.reviewed_at && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  → <span className={`h-1.5 w-1.5 rounded-full ${r.status === "approved" ? "bg-emerald-500" : r.status === "rejected" ? "bg-rose-500" : "bg-sky-500"}`} />
                  {r.status === "approved" ? "Aprovada" : r.status === "rejected" ? "Rejeitada" : "Em análise"}
                  <span className="text-foreground">{fmtDate(r.reviewed_at)}</span>
                </span>
              )}
              {r.reviewer_name && <span className="text-muted-foreground">por <b>{r.reviewer_name}</b></span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================ */
/*  PROFILE                                                     */
/* ============================================================ */
export function EmployeeProfile() {
  const { employee, refresh } = useEmployeeAuth();
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(employee?.photo_data_url || "");
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "", visible: false });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    setForm({
      phone: employee?.phone || "",
      whatsapp: employee?.whatsapp || employee?.phone || "",
      address: employee?.address || "",
      city: employee?.city || "",
      state: employee?.state || "",
      zip: employee?.zip || employee?.cep || "",
      emergency_contact_name: employee?.emergency_contact_name || "",
      emergency_contact_phone: employee?.emergency_contact_phone || "",
    });
    setPhoto(employee?.photo_data_url || "");
  }, [employee]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Foto maior que 2 MB."); return; }
    const fr = new FileReader();
    fr.onload = () => setPhoto(fr.result);
    fr.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (photo !== (employee?.photo_data_url || "")) body.photo_data_url = photo;
      await employeeApi.patch("/employee-portal/me", body);
      toast.success("Perfil atualizado");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const changePwd = async (e) => {
    e?.preventDefault?.();
    if (!pwd.current || !pwd.next) { toast.error("Preencha as duas senhas"); return; }
    if (pwd.next.length < 8) { toast.error("Nova senha precisa de 8+ caracteres"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("As senhas não coincidem"); return; }
    setPwdSaving(true);
    try {
      await employeeApi.patch("/employee-portal/me", { current_password: pwd.current, password: pwd.next });
      toast.success("Senha alterada com sucesso.");
      setPwd({ current: "", next: "", confirm: "", visible: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao alterar"); }
    finally { setPwdSaving(false); }
  };
  const strength = pwdStrength(pwd.next);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div data-testid="employee-page-profile" className="space-y-5">
      <PageHeader title="Meu Perfil"
                  subtitle="Mantenha seus dados de contato, endereço e emergência sempre atualizados."
                  icon={UserCircle} />

      {/* Dados institucionais (somente leitura) */}
      <div className="rounded-2xl border border-border bg-white p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <BadgeCheck className="h-4 w-4 text-primary" /> Dados institucionais
        </h3>
        <div className="flex items-start gap-4 mb-4 flex-wrap">
          <div className="h-24 w-24 rounded-full border border-border bg-muted overflow-hidden grid place-items-center shrink-0">
            {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <User className="h-8 w-8 text-muted-foreground" />}
          </div>
          <label className="text-xs cursor-pointer text-primary hover:underline flex items-center gap-1 self-end mb-1">
            <Upload className="h-3.5 w-3.5" /> Trocar foto
            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo" />
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldRO label="Nome completo" value={employee?.full_name} icon={User} />
          <FieldRO label="Matrícula" value={employee?.employee_code} icon={IdCard} />
          <FieldRO label="Cargo" value={employee?.position} icon={Briefcase} />
          <FieldRO label="Departamento" value={employee?.department} icon={Building2} />
          <FieldRO label="E-mail corporativo" value={employee?.email} icon={MailCheck} />
          <FieldRO label="Data de admissão" value={fmtDate(employee?.admission_date || employee?.hire_date)} icon={CalendarIcon} />
        </div>
      </div>

      {/* Dados editáveis */}
      <div className="rounded-2xl border border-border bg-white p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-primary" /> Dados pessoais e contato
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Telefone" icon={Phone}>
            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}
                   placeholder="(00) 00000-0000" data-testid="profile-phone" />
          </Field>
          <Field label="WhatsApp" icon={Phone}>
            <Input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)}
                   placeholder="(00) 00000-0000" data-testid="profile-whatsapp" />
          </Field>
          <Field label="Endereço" icon={MapPin} className="md:col-span-2">
            <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)}
                   placeholder="Rua, número, complemento" data-testid="profile-address" />
          </Field>
          <Field label="Cidade" icon={Building2}>
            <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="profile-city" />
          </Field>
          <Field label="Estado" icon={Building2}>
            <Input value={form.state || ""} onChange={(e) => set("state", e.target.value)}
                   maxLength={2} placeholder="UF" data-testid="profile-state" />
          </Field>
          <Field label="CEP" icon={MapPin}>
            <Input value={form.zip || ""} onChange={(e) => set("zip", e.target.value)}
                   placeholder="00000-000" data-testid="profile-zip" />
          </Field>
          <Field label="Contato de emergência" icon={AlertCircle}>
            <Input value={form.emergency_contact_name || ""} onChange={(e) => set("emergency_contact_name", e.target.value)}
                   placeholder="Nome" data-testid="profile-ecn" />
          </Field>
          <Field label="Telefone de emergência" icon={Phone}>
            <Input value={form.emergency_contact_phone || ""} onChange={(e) => set("emergency_contact_phone", e.target.value)}
                   placeholder="(00) 00000-0000" data-testid="profile-ecp" />
          </Field>
        </div>
        <div className="flex justify-end pt-2 border-t border-border">
          <Button onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvando</> : "Salvar alterações"}
          </Button>
        </div>
      </div>

      {/* Alterar senha */}
      <form onSubmit={changePwd} className="rounded-2xl border border-border bg-white p-5 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" /> Alterar senha
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="text-xs">Senha atual</Label>
            <Input type={pwd.visible ? "text" : "password"} value={pwd.current}
                   onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                   className="mt-1" data-testid="profile-pwd-old" /></div>
          <div>
            <Label className="text-xs">Nova senha</Label>
            <Input type={pwd.visible ? "text" : "password"} value={pwd.next}
                   onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                   className="mt-1" minLength={8} data-testid="profile-pwd-new" />
            {pwd.next && (
              <div className="mt-1 flex items-center gap-2" data-testid="pwd-strength">
                <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full transition-all ${strength.cls}`} style={{ width: `${strength.pct}%` }} />
                </div>
                <span className={`text-[10px] font-medium ${strength.textCls}`}>{strength.label}</span>
              </div>
            )}
          </div>
          <div><Label className="text-xs">Confirmar nova senha</Label>
            <Input type={pwd.visible ? "text" : "password"} value={pwd.confirm}
                   onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                   className="mt-1" data-testid="profile-pwd-confirm" /></div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={pwd.visible} onChange={(e) => setPwd({ ...pwd, visible: e.target.checked })} />
            Mostrar senhas
          </label>
          <Button type="submit" variant="outline" disabled={pwdSaving} data-testid="profile-pwd-submit">
            {pwdSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Alterando</> : "Alterar senha"}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          A senha deve ter no mínimo 8 caracteres. Recomendamos usar letras, números e símbolos.
        </p>
      </form>
    </div>
  );
}

function Field({ label, icon: Icon, className = "", children }) {
  return (
    <div className={className}>
      <Label className="text-xs flex items-center gap-1 mb-1">{Icon && <Icon className="h-3 w-3" />} {label}</Label>
      {children}
    </div>
  );
}
function FieldRO({ label, value, icon: Icon }) {
  return (
    <div>
      <Label className="text-xs flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />} {label}</Label>
      <div className="mt-1 h-9 rounded-md border border-input bg-muted/40 px-3 text-sm flex items-center truncate">
        {value || "—"}
      </div>
    </div>
  );
}
function pwdStrength(pwd) {
  if (!pwd) return { pct: 0, cls: "bg-transparent", textCls: "text-muted-foreground", label: "" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  const map = [
    { pct: 15, cls: "bg-rose-500", textCls: "text-rose-600", label: "Muito fraca" },
    { pct: 30, cls: "bg-rose-500", textCls: "text-rose-600", label: "Fraca" },
    { pct: 55, cls: "bg-amber-500", textCls: "text-amber-600", label: "Razoável" },
    { pct: 75, cls: "bg-primary", textCls: "text-primary", label: "Boa" },
    { pct: 100, cls: "bg-emerald-500", textCls: "text-emerald-600", label: "Forte" },
  ];
  return map[Math.min(score, map.length) - 1] || map[0];
}

/* ============================================================ */
/*  NOTIFICATIONS                                               */
/* ============================================================ */
export function EmployeeNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread | read | broadcast | direct
  const [readMap, setReadMap] = useState(readNotesMap);
  useEffect(() => {
    setLoading(true);
    employeeApi.get("/employee-portal/notifications")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const markRead = (id) => {
    if (readMap[id]) return;
    const m = { ...readMap, [id]: new Date().toISOString() };
    setReadMap(m); saveReadNotes(m);
  };
  const markAllRead = () => {
    const m = { ...readMap };
    let changed = false;
    items.forEach((n) => { if (!m[n.id]) { m[n.id] = new Date().toISOString(); changed = true; } });
    if (changed) { setReadMap(m); saveReadNotes(m); toast.success("Notificações marcadas como lidas."); }
    else toast.info("Nenhuma notificação nova.");
  };
  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return items.filter((n) => {
      if (filter === "unread" && readMap[n.id]) return false;
      if (filter === "read" && !readMap[n.id]) return false;
      if (filter === "broadcast" && !n.broadcast) return false;
      if (filter === "direct" && n.broadcast) return false;
      if (!kw) return true;
      return (n.title || "").toLowerCase().includes(kw) || (n.body || "").toLowerCase().includes(kw);
    });
  }, [items, filter, q, readMap]);
  const unreadCount = items.filter((n) => !readMap[n.id]).length;
  return (
    <div data-testid="employee-page-notifications" className="space-y-5">
      <PageHeader title="Central de Notificações"
                  subtitle={unreadCount > 0
                    ? `Você tem ${unreadCount} notificação${unreadCount === 1 ? "" : "s"} não lida${unreadCount === 1 ? "" : "s"}.`
                    : "Você está em dia com todas as notificações."}
                  icon={Bell}
                  right={unreadCount > 0 ? (
                    <Button size="sm" variant="outline" onClick={markAllRead}
                            data-testid="notif-mark-all-read">
                      <MailCheck className="h-4 w-4 mr-1" /> Marcar todas como lidas
                    </Button>
                  ) : null} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Não lidas" value={unreadCount} icon={Bell} tone={unreadCount > 0 ? "rose" : "primary"}
                 onClick={() => setFilter("unread")} testid="notif-kpi-unread" />
        <KpiCard label="Lidas" value={items.length - unreadCount} icon={CheckCircle2} tone="emerald"
                 onClick={() => setFilter("read")} testid="notif-kpi-read" />
        <KpiCard label="Comunicados gerais" value={items.filter((n) => n.broadcast).length}
                 icon={AlertCircle} tone="sky"
                 onClick={() => setFilter("broadcast")} testid="notif-kpi-broadcast" />
        <KpiCard label="Direcionadas" value={items.filter((n) => !n.broadcast).length}
                 icon={UserCircle} tone="violet"
                 onClick={() => setFilter("direct")} testid="notif-kpi-direct" />
      </div>

      <div className="rounded-2xl border border-border bg-white p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Buscar notificações" className="pl-8" data-testid="notif-search" />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {[
            { v: "all", l: "Todas" },
            { v: "unread", l: "Não lidas" },
            { v: "read", l: "Lidas" },
            { v: "broadcast", l: "Gerais" },
            { v: "direct", l: "Diretas" },
          ].map((f) => (
            <Button key={f.v} size="sm" variant={filter === f.v ? "default" : "outline"}
                    onClick={() => setFilter(f.v)}
                    data-testid={`notif-filter-${f.v}`}>{f.l}</Button>
          ))}
        </div>
      </div>

      {loading ? <SkeletonList n={5} testid="notif-loading" />
       : filtered.length === 0 ? <EmptyState label="Nenhuma notificação encontrada"
                                              hint="Ajuste os filtros ou aguarde novas comunicações." icon={Bell} />
       : (
        <ul className="space-y-2" data-testid="notifications-list">
          {filtered.map((n) => {
            const isRead = !!readMap[n.id];
            return (
              <li key={n.id}
                  onClick={() => markRead(n.id)}
                  className={`rounded-2xl border p-4 cursor-pointer transition ${isRead ? "border-border bg-white" : "border-primary/40 bg-primary/[0.03] shadow-sm"}`}
                  data-testid={`notif-row-${n.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 border ${
                    n.broadcast ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-violet-50 text-violet-700 border-violet-200"
                  }`}>
                    {n.broadcast ? <AlertCircle className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm ${isRead ? "font-normal" : "font-semibold"} truncate`}>{n.title}</p>
                      {!isRead && <span className="h-2 w-2 rounded-full bg-primary" title="Não lida" />}
                    </div>
                    {n.body && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{n.body}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1.5">
                      {fmtDate(n.created_at)} · {fmtTime(n.created_at)} · {n.broadcast ? "Comunicado geral" : "Direcionada a você"}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* Legacy helpers preservados para compatibilidade */
function PageTitle({ title, subtitle }) {
  return (
    <div>
      <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
function EmptyBox({ label }) {
  return <div className="rounded-2xl border border-dashed border-border bg-white px-8 py-10 text-center text-sm text-muted-foreground">{label}</div>;
}
